import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { createTheme } from '@mui/material/styles';
import * as yup from 'yup';
import InputWithAutocomplete from '../../Components/common/Form/InputWithAutocomplete/InputWithAutocomplete';
import api from '../../api/apiSettings';
import { cvUpdate, restoreAfterRefresh } from '../../actions/uploadData';
import ProfileBuilderWrapper from '../../Components/pages/profileBuilder.Wrapper/ProfileBuilderWrapper';
import { maxSymbolQTY, minSymbolQTY, MOBILE_BREAKPOINT, validationMessages } from '../../utils/constants';
import ValidationErrorLabel from '../../Components/common/ValidationErrorLabel/ValidationErrorLabel';
import { useWindowSize } from '../../utils/hooks';
import colors from '../../scss/colors';

export function ProfileBuilderFour() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const size = useWindowSize();
  const personalDetails = useSelector((state) => state.uploadData.parsedCV.PersonalDetails);
  const allCvUploadStore = useSelector((state) => state.uploadData.parsedCV);
  const { t } = useTranslation();
  const validationMessage = useMemo(() => validationMessages(t), [t]);
  const [autocompleteCountry, setAutocompleteCountry] = useState([]);
  const [autocompleteState, setAutocompleteState] = useState([]);
  const [autocompleteCities, setAutocompleteCities] = useState([]);
  const [customError, setCustomError] = useState(false);

  const clearSuggestions = (entity) => {
    switch (entity) {
      case 'country':
        setAutocompleteCountry([]);
        break;
      case 'state':
        setAutocompleteState([]);
        break;
      case 'city':
        setAutocompleteCities([]);
        break;
      default:
        break;
    }
  };

  const validationSchema = yup.object({
    country: yup.string().test('required', validationMessage.COUNTRY_REQ, (value) => value),
    city: yup.string().test('required', validationMessage.CITY_REQ, (value) => value),
    phone: yup
      .string()
      .nullable()
      .required(validationMessage.PHONE_NUMBER_REQ)
      .test('min-max-length', validationMessage.PHONE_NUMBER_INVALID, (value) => {
        if (!value?.includes('+')) {
          return value?.length >= minSymbolQTY.PHONE && value?.length <= maxSymbolQTY.PHONE;
        }
        const phoneNumber = value.slice(value.indexOf(' '))?.replace(/[^0-9]/g, '');
        return phoneNumber?.length >= minSymbolQTY.PHONE && phoneNumber?.length <= maxSymbolQTY.PHONE;
      }),
    phoneCode: yup.string().test('required-country-code', validationMessage.PHONE_NUMBER_INVALID, (value) => {
      return value !== '+undefined';
    })
  });

  const setFormToState = (values) => {
    dispatch(
      cvUpdate({
        flag: 'Personal',
        value: {
          PhoneCode: values.phoneCode,
          Phone: values.phone,
          Country: values.country,
          State: values.state,
          City: values.city
        }
      })
    );
  };

  const onSubmit = async (values) => {
    const countryFromApi = await api.get(`v1/get-countries?keyword=${values.country}`);
    if (values.country !== countryFromApi.data?.Data?.[0]?.Name) {
      setCustomError(validationMessage?.VALIDATION_LOCATION_COUNTRY);
      return;
    }

    if (values.state) {
      const stateFromApi = await api.get(
        `v1/get-states-by-country?countryName=${values.country}&keyword=${values.state}`
      );
      if (values.state !== stateFromApi.data?.Data?.[0]?.Name) {
        setCustomError(validationMessage?.VALIDATION_LOCATION_STATE);
        return;
      }
    }

    const cityFromApi = await api.get(
      `v1/get-cities-by-state?countryName=${values.country}&stataName=${values.state}&keyword=${values.city}`
    );
    if (values.city !== cityFromApi.data?.Data?.[0]?.Name) {
      setCustomError(validationMessage.VALIDATION_LOCATION_CITY);
      return;
    }

    setFormToState(values);
    navigate('/dashboard/profile-builder-five');
  };

  const personalDetailsForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      country: personalDetails?.Country || '',
      state: personalDetails?.State || '',
      city: personalDetails?.City || '',
      phone: personalDetails?.Phone?.startsWith('+') ? personalDetails.Phone.split(/ (.*)/)[1] : personalDetails.Phone,
      phoneCode: personalDetails?.PhoneCode || personalDetails.Phone?.split(/ (.*)/)[0],
      fullPhone: personalDetails?.PhoneCode
        ? `${personalDetails?.PhoneCode}${personalDetails?.Phone}`?.replace(/[^0-9]/g, '')
        : personalDetails?.Phone?.replace(/[^0-9]/g, '')
    },
    validationSchema,
    onSubmit: (values) => onSubmit(values)
  });

  const backHandler = () => {
    setFormToState(personalDetailsForm.values);
    navigate('/dashboard/profile-builder-three');
  };

  const handlersByEntity = {
    country: async (value) => {
      if (!value) {
        personalDetailsForm.setFieldValue('state', '');
        setAutocompleteState([]);
        personalDetailsForm.setFieldValue('city', '');
        setAutocompleteCities([]);
      }
      if (value !== personalDetailsForm.values.country) {
        personalDetailsForm.setFieldValue('state', '');
        personalDetailsForm.setFieldValue('city', '');
      }
      personalDetailsForm.setFieldValue('country', value);
      if (!value || value.length < minSymbolQTY.COUNTRY) return;
      const result = await api.get(`v1/get-countries?keyword=${value}`);
      setAutocompleteCountry(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },
    state: async (value) => {
      if (personalDetailsForm.values.country === '') return;
      personalDetailsForm.setFieldValue('state', value);
      if (!value || value.length < minSymbolQTY.STATE) return;
      const result = await api.get(
        `v1/get-states-by-country?countryName=${personalDetailsForm.values.country}&keyword=${value}`
      );
      setAutocompleteState(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    },
    city: async (value) => {
      personalDetailsForm.setFieldValue('city', value);
      if (!value || value.length < minSymbolQTY.CITY) return;
      const result = await api.get(`v1/get-cities-by-state?countryName=${personalDetailsForm.values.country}
        &stataName=${personalDetailsForm.values.state}&keyword=${value}`);
      setAutocompleteCities(result?.data?.Data?.map((item) => ({ label: item?.Name })) || []);
    }
  };

  const inputHandler = ({ entity, value }) => {
    handlersByEntity[entity](value);
  };

  const handleRefresh = () => {
    localStorage.setItem(
      'forRestoreStore',
      JSON.stringify({
        ...allCvUploadStore,
        PersonalDetails: {
          ...allCvUploadStore.PersonalDetails,
          Country: personalDetailsForm.values.country,
          State: personalDetailsForm.values.state,
          City: personalDetailsForm.values.city,
          Phone: personalDetailsForm.values.phone,
          PhoneCode: personalDetailsForm.values.phoneCode
        }
      })
    );
  };

  const restoreData = () => {
    const data = localStorage.getItem('forRestoreStore');

    if (data) {
      dispatch(restoreAfterRefresh({ ...JSON.parse(data) }));
      localStorage.setItem('forRestoreStore', '');
    }
  };

  useEffect(() => {
    personalDetailsForm.setFieldValue('country', personalDetails.Country);
    personalDetailsForm.setFieldValue('state', personalDetails.State);
    personalDetailsForm.setFieldValue('city', personalDetails.City);
    setAutocompleteCountry([]);
    setAutocompleteState([]);
    setAutocompleteCities([]);
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', handleRefresh);
    restoreData();
    return () => {
      window.removeEventListener('beforeunload', handleRefresh);
    };
  }, [personalDetailsForm.values]);

  const autoCompleteStyles = {
    '& legend': { display: 'none' },
    '& fieldset': { top: 0 },
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      padding: '2px'
    }
  };

  const theme = createTheme({
    palette: {
      text: {
        disabled: colors.BLACK_TXT
      }
    }
  });

  return (
    <ProfileBuilderWrapper
      titleMain={t('profile.builder.start.complete')}
      titleSecondary={t('profile.builder.confirmpersonal')}
      text={t('profile.builder.founddetails')}
      progress={67}
    >
      <div className="row py-4">
        <div className="col-8 offset-2 text-start">
          <div className="row pt-4">
            <h6>{t('common.forms.phone')}</h6>
            <div className="col">
              <PhoneInput
                inputStyle={{ width: '100%', fontSize: '16px' }}
                value={personalDetailsForm.values.fullPhone}
                onChange={(newPhone, countryData) => {
                  personalDetailsForm.setFieldValue('fullPhone', newPhone);
                  personalDetailsForm.setFieldValue('phoneCode', `+${countryData.dialCode}`);
                  personalDetailsForm.setFieldValue(
                    'phone',
                    `${newPhone}`.replace(new RegExp(`${countryData.dialCode}`), '')
                  );
                }}
              />
              {personalDetailsForm.touched.phone && personalDetailsForm.errors.phone && (
                <ValidationErrorLabel message={personalDetailsForm.errors.phone} />
              )}
              {personalDetailsForm.touched.phoneCode &&
                personalDetailsForm.errors.phoneCode &&
                !personalDetailsForm.errors.phone && (
                  <ValidationErrorLabel message={personalDetailsForm.errors.phoneCode} />
                )}
            </div>
          </div>
          <div className="row pt-4">
            {customError && (
              <div style={{ marginBottom: '10px' }}>
                <ValidationErrorLabel message={customError} />
              </div>
            )}
            <h6>{t('common.forms.country')}*</h6>
            <div className="col-12">
              <InputWithAutocomplete
                entity="country"
                error={personalDetailsForm.touched.city && personalDetailsForm.errors.country}
                theme={theme}
                autoCompleteStyles={autoCompleteStyles}
                disabled={false}
                autocompleteValues={autocompleteCountry}
                fieldValue={personalDetailsForm.values.country}
                changeHandler={inputHandler}
                placeholder={`${t('common.forms.country')}*`}
                clearSuggestions={clearSuggestions}
              />
              {personalDetailsForm.values.country.length !== 0 &&
                personalDetailsForm.values.country.length < minSymbolQTY.COUNTRY && (
                  <ValidationErrorLabel message={validationMessage.COUNTRY_MIN_CHAR} />
                )}
            </div>
          </div>
          <div className="row pt-4">
            <h6>{t('common.forms.location')}</h6>
            <div className="col-12 mb-4">
              <InputWithAutocomplete
                entity="city"
                error={personalDetailsForm.touched.city && personalDetailsForm.errors.city}
                theme={theme}
                autoCompleteStyles={autoCompleteStyles}
                disabled={!personalDetailsForm.values.country}
                autocompleteValues={autocompleteCities}
                fieldValue={personalDetailsForm.values.city}
                changeHandler={inputHandler}
                placeholder={`${t('common.forms.city')}*`}
                clearSuggestions={clearSuggestions}
              />
              {personalDetailsForm.values.city.length !== 0 &&
                personalDetailsForm.values.city.length < minSymbolQTY.CITY && (
                  <ValidationErrorLabel message={validationMessage.CITY_MIN_CHAR} />
                )}
            </div>
            <h6>{t('common.forms.optional')}</h6>
            <div className="col-12 pb-4">
              <InputWithAutocomplete
                entity="state"
                error={null}
                theme={theme}
                autoCompleteStyles={autoCompleteStyles}
                disabled={false}
                autocompleteValues={autocompleteState}
                fieldValue={personalDetailsForm.values.state}
                changeHandler={inputHandler}
                placeholder={t('common.forms.state')}
                clearSuggestions={clearSuggestions}
              />
              {personalDetailsForm.values.state.length !== 0 &&
                personalDetailsForm.values.state.length < minSymbolQTY.STATE && (
                  <ValidationErrorLabel message={validationMessage.STATE_MIN_CHAR} />
                )}
            </div>
          </div>
        </div>
      </div>
      <div className={`row ${size.width > MOBILE_BREAKPOINT ? 'pt-5' : ''}`}>
        <div className="col-6 text-start">
          <button onClick={backHandler} className="outline-button text-decoration-none p-3 px-5 mt-4">
            <span className="d-inline text-capitalize">{t('common.general.back')}</span>
          </button>
        </div>
        <div className="col-6 text-end">
          <button onClick={personalDetailsForm.handleSubmit} className="button p-3 px-5 mt-4">
            <span className="d-inline text-capitalize">{t('common.general.next')}</span>
          </button>
        </div>
      </div>
    </ProfileBuilderWrapper>
  );
}
